import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Cell,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { GetMemberWidgets } from "features/libraryDashboard/api/hooks";
import React, { useEffect, useState } from "react";
import {
  IMemberResult,
  IMemberWidgetSchema,
  MemberWidgetSchema,
} from "features/libraryDashboard/api/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import useMemberWidgetStore from "features/libraryDashboard/store/MemberStatistics";
import RadioButtonGroup from "components/RadioButtonGroup";

const MemberStatistics = () => {
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#9d3be3",
    "#FFBB28",
    "#FF8042",
    "#52D726",
    "#FFEC00",
    "#FF7300",
    "#007ED6",
    "#7CDDDD",
    "#64C2A6",
    "#FF2E7E",
  ];
  const { postModel, handleFiltering } = useMemberWidgetStore();

  const { control, watch } = useForm<IMemberWidgetSchema>({
    defaultValues: { filterOptions: 2 },
    resolver: zodResolver(MemberWidgetSchema),
  });

  const { data: result, isLoading, isFetching } = GetMemberWidgets(postModel);
  const [members, setMembers] = useState<IMemberResult>();
  const FilterOptions = [
    { id: "1", label: "All" },
    { id: "2", label: "Active" },
    { id: "3", label: "De-Active" },
  ];

  useEffect(() => {
    if (result?.status === 200) {
      setMembers(result?.apiData);
    }
  }, [result]);

  const filterOptions = watch("filterOptions");

  useEffect(() => {
    handleFiltering({ filterOptions: filterOptions });
  }, [filterOptions]);

  const isDataLoading = isLoading || isFetching;

  const pieData =
    members?.chartResult?.map((d) => ({
      name: d.title,
      value: d.count,
    })) || [];

  const legendData = pieData?.map((item, index) => ({
    name: item.name,
    color: COLORS[index % COLORS.length],
  }));

  const CustomLegend = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px",
        width: "100%",
        maxHeight: 250,
        overflowY: "auto",
      }}
    >
      {legendData?.map((item, index) => (
        <div
          key={`legend-item-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: item.color,
            }}
          ></div>
          <span style={{ wordBreak: "break-word" }}>{item.name}</span>
        </div>
      ))}
    </div>
  );

  const sortedData = members?.tabularResult
    ? [...members.tabularResult].sort((a) => {
        if (a.title === "Total Member") return -1;
        return 1;
      })
    : [];

  const totalMemberCount =
    sortedData.find((a) => a.title === "Total Member")?.count ??
    sortedData[0]?.count ??
    0;

  return (
    <>
      <Grid>
        <Typography variant="h2">Members</Typography>
      </Grid>
      <Grid container spacing={2.5} sx={{ my: 1 }}>
        <Grid item sm={12} md={12} xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {isDataLoading ? (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 250,
                          }}
                        >
                          <Skeleton
                            variant="circular"
                            width={180}
                            height={180}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            p: 1,
                            width: "100%",
                            height: 250,
                            justifyContent: "center",
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((item) => (
                            <Box
                              key={item}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                              }}
                            >
                              <Skeleton
                                variant="circular"
                                width={12}
                                height={12}
                                sx={{ flexShrink: 0 }}
                              />
                              <Skeleton
                                variant="text"
                                width="75%"
                                height={20}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <ResponsiveContainer height={250}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              innerRadius={60}
                              outerRadius={100}
                              fill="#8884d8"
                              paddingAngle={2}
                              dataKey="value"
                              minAngle={15}
                            >
                              {pieData?.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                              <Label
                                value="Total"
                                position="center"
                                fontSize="14"
                                fontWeight="bold"
                                fill="#333"
                              />
                              <Label
                                value={totalMemberCount.toLocaleString("en-IN")}
                                position="center"
                                fontSize="18"
                                fontWeight="bold"
                                fill="#333"
                                dy={20}
                              />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomLegend />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  sx={{ minHeight: 250 }}
                >
                  {/* Radio buttons in upper right */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "center", sm: "flex-end" },
                      width: "100%",
                      mb: 2,
                    }}
                  >
                    <RadioButtonGroup
                      row
                      control={control}
                      name="filterOptions"
                      options={FilterOptions}
                    />
                  </Box>
                  {/* Centered statistics */}
                  {isDataLoading ? (
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                      sx={{ fontSize: "1.2rem", flexGrow: 1, my: "auto" }}
                    >
                      {[1, 2, 3, 4].map((item, index) => (
                        <React.Fragment key={item}>
                          <Grid item textAlign="center">
                            <Skeleton
                              variant="text"
                              width={80}
                              height={20}
                              sx={{ mx: "auto" }}
                            />
                            <Skeleton
                              variant="text"
                              width={60}
                              height={36}
                              sx={{ mx: "auto" }}
                            />
                          </Grid>
                          {index < 3 && (
                            <Grid item>
                              <Typography variant="h5" color="text.secondary">
                                {index === 0 ? "=" : "+"}
                              </Typography>
                            </Grid>
                          )}
                        </React.Fragment>
                      ))}
                    </Grid>
                  ) : (
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                      sx={{ fontSize: "1.2rem", flexGrow: 1 }}
                    >
                      {sortedData?.map((item, index) => (
                        <React.Fragment key={index}>
                          <Grid item textAlign="center">
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              {item.title}
                            </Typography>
                            <Typography variant="h4" color="text.primary">
                              {item.count.toLocaleString("en-IN")}
                            </Typography>
                          </Grid>
                          {/* Add symbols ('=', '+') dynamically */}
                          {index < sortedData.length - 1 && (
                            <Grid item>
                              <Typography variant="h5" color="text.secondary">
                                {index === 0 ? "=" : "+"}
                              </Typography>
                            </Grid>
                          )}
                        </React.Fragment>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
export default MemberStatistics;
